import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { scanForEvseWs } from '../../../../../installation/api/ws/scan-for-evse-ws'
import { logError, logMessage } from '../../../../../utils'
import { AuthContext } from '@e3dc-react/shell/Libs/authentication'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { getCleanMacAddressPairingId } from '../../../utils'

type ModbusTcpResult = {
  mac: string
  deviceName: string
  ip: string
  /**
   * - 6 or null: "Cintech" (default)
   * - 7: "EEbus" (Hager | Audi)
   * - 8: "WittyLink"
   */
  subtype: 6 | 7 | 8 | null
  /**
   * TODO: Fill in the sub types.
   */
  reference: string
}

interface EvseScanModel {
  media: string
  results: {
    firstScanComplete: boolean
    isPartialResult: boolean
    devices: ModbusTcpResult[]
  }
}
//#region Editor

interface ModbusTcpPairingEditorProps {
  installationId: number
  emcId: string
  evseDeviceId: string
  currentPairingId: string | undefined
  setPairingResult: (result: { pairingId: string; deviceReference: string; ip: string }) => void
  usedCleanPairingIds: string[]
}

/** Dialog content for Modbus TCP pairing on evse charging stations */
export const ModbusTcpPairingEditor: React.FC<ModbusTcpPairingEditorProps> = ({
  installationId,
  emcId,
  evseDeviceId,
  currentPairingId,
  setPairingResult,
  usedCleanPairingIds,
}) => {
  const { t } = useCustomTranslation()

  const [error, setError] = useState<{ code: number; message: string } | null>(null)
  const [data, setData] = useState<EvseScanModel | null>(null)

  const isLoadingFirstScan = data?.results?.devices?.length === 0 && !data?.results?.firstScanComplete
  const isLoading = (!data && !error) || isLoadingFirstScan

  const { authToken } = useContext(AuthContext)

  useEffect(() => {
    if (!authToken) return

    const evseWebSocket = scanForEvseWs(installationId, emcId, evseDeviceId, authToken)

    evseWebSocket.onopen = () => logMessage('Evse pairing socket: connected')
    evseWebSocket.onclose = () => logMessage('Evse pairing socket: disconnected')
    evseWebSocket.onerror = e => logError('Evse pairing socket error:', e)

    evseWebSocket.onmessage = e => {
      const eventData = JSON.parse(e.data)

      if (eventData.statusCode === 204 && Object.keys(eventData).length === 1) {
        setData(null)
        return
      }

      if (eventData.statusCode === 408) {
        setData(null)
        setError({ code: eventData.statusCode, message: eventData.error })
        return
      }

      if (eventData.statusCode === 400) {
        // Unknown connection error, so display last successful result + errorMessage
        setError({
          code: eventData.statusCode,
          message: eventData.error ?? t('installation.productEditor.rscpError'),
        })
        return
      }

      // Here, all the errors are already handled. This message contains data.
      setData(eventData)
    }

    return () => {
      evseWebSocket.close()
    }
  }, [installationId, emcId, evseDeviceId, t, authToken])

  if (error) {
    return <Typography color="error">{error.message}</Typography>
  }

  if (isLoading || !data) {
    return (
      <Box textAlign={'center'} pt={1}>
        <CircularProgress />
      </Box>
    )
  }

  if (!data?.results?.devices?.length) {
    return <Alert severity="error" text={t('installation.productEditor.scanEvses.noResults')} />
  }

  return (
    <Box maxHeight={500}>
      {data.results.devices.map(result => {
        const { mac, reference, ip } = result

        const cleanCurrentPairingId = getCleanMacAddressPairingId(currentPairingId || '')
        const cleanMac = getCleanMacAddressPairingId(mac)

        return (
          <NetworkScanResult
            key={mac}
            result={result}
            handleSelect={() => setPairingResult({ pairingId: mac, deviceReference: reference, ip })}
            isSelected={cleanCurrentPairingId === cleanMac}
            disabled={usedCleanPairingIds.includes(cleanMac)}
          />
        )
      })}
    </Box>
  )
}
//#endregion

//#region NetworkScan results
interface NetworkScanResultProps {
  result: ModbusTcpResult
  handleSelect: () => void
  isSelected: boolean
  disabled: boolean
}

const NetworkScanResult: React.FC<NetworkScanResultProps> = ({ result, handleSelect, isSelected, disabled }) => {
  const { t } = useCustomTranslation()

  const name = result.deviceName
  const pairingId = result.ip

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: theme => `1px solid ${theme.palette.grey[500]}`,
        py: 2,
        flexDirection: { xs: 'column', sm: 'initial' },
        alignItems: { xs: 'normal', sm: 'center' },
      }}
    >
      <Box>
        <Box display={'flex'}>
          <Title>{t('common.name')}</Title>
          <PairingId>{name}</PairingId>
        </Box>
        <Box display={'flex'}>
          <Title>{t(`installation.productEditor.scanNetwork.mac`)}</Title>
          <PairingId>{result.mac}</PairingId>
        </Box>
        <Box display={'flex'}>
          <Title>{t('installation.productEditor.scanNetwork.ip')}</Title>
          <PairingId>{pairingId}</PairingId>
        </Box>
        <Box display={'flex'}>
          <Title>{t('installation.productEditor.scanNetwork.reference')}</Title>
          <PairingId>{result.reference}</PairingId>
        </Box>
      </Box>
      <Button color={'secondary'} variant={'outlined'} size="large" onClick={handleSelect} disabled={isSelected || disabled}>
        {isSelected ? t('installation.productEditor.scanNetwork.selected') : t('installation.productEditor.scanNetwork.select')}
      </Button>
    </Box>
  )
}

const Title: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ minWidth: { xs: 60, sm: 100, lineHeight: 1.5 } }}>{children}</Typography>
)

const PairingId: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontWeight: 'bold',
      lineHeight: 1.5,
      maxWidth: { xs: '80%', sm: undefined },
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    {children}
  </Typography>
)
//#endregion
